import { computed, type Ref, ref } from "vue";

import type { CustomFormSubmissionQuestion } from "@/typings/customForm";
import { answerDisplayValue } from "./customFormAnswerDisplay";

export interface SearchableSection {
  questions: CustomFormSubmissionQuestion[];
}

// What a question matches on. Includes the rendered answer so searching for
// what a tech wrote finds the question that holds it — the same text the row
// prints, which is why answerDisplayValue is shared rather than reimplemented.
//
// Two carve-outs, both of which fall back to the question's own words:
//   * signature and photo_only render no text at all
//   * checkbox renders "Yes"/"No", so a search for "no" hits every unchecked
//     box in the form. That is what the answer says, so it stays matchable.
function haystack(question: CustomFormSubmissionQuestion): string {
  return [
    question.title,
    question.description,
    answerDisplayValue(question, question.answer)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function useFormSearch<T extends SearchableSection>(sections: Ref<T[]>) {
  const query = ref("");

  const term = computed(() => query.value.trim().toLowerCase());
  const active = computed(() => term.value.length > 0);

  // Sections with no surviving question drop out entirely — a heading over
  // nothing reads as a section that has no matches rather than one to scroll past.
  const filtered = computed<T[]>(() => {
    if (!active.value) return sections.value;

    return sections.value
      .map(
        (row) =>
          ({
            ...row,
            questions: row.questions.filter((question) =>
              haystack(question).includes(term.value)
            )
          }) as T
      )
      .filter((row) => row.questions.length > 0);
  });

  const matchCount = computed(() =>
    filtered.value.reduce((total, row) => total + row.questions.length, 0)
  );

  function clear() {
    query.value = "";
  }

  return { query, active, filtered, matchCount, clear };
}
